import { useEffect, useRef, useState } from 'react'

/**
 * The MBI mark.
 *
 * Drawn rather than imported: this runs on a locked-down corporate desktop that
 * may have no outbound network for an icon font or a CDN, and a platform whose
 * identity fails to load looks broken in exactly the moment it should look
 * trustworthy.
 *
 * Four ascending bars with the last cut short. Geometric rather than
 * illustrative because it has to survive at 20px in a browser tab.
 *
 * NO CLIENT NAME ANYWHERE IN THIS FILE. The mark and the wordmark are the
 * product's, not a customer's — the same build is meant to stand up in front of
 * anyone, and a lockup that names one company cannot.
 */
export function BrandMark({
  size = 28,
  tone = 'navy',
  animate = false,
}: {
  size?: number
  tone?: 'navy' | 'white'
  /** Grows the bars from the baseline on mount. Sign-in only. */
  animate?: boolean
}) {
  const bg = tone === 'navy' ? 'var(--color-navy-700)' : '#ffffff'
  const fg = tone === 'navy' ? '#ffffff' : 'var(--color-navy-700)'

  // Staggered so they arrive as a sequence rather than a block — the same
  // left-to-right reading order as the chart they represent.
  const bars = [
    { x: 7, y: 18, h: 7, o: 0.55, d: 0 },
    { x: 12.25, y: 14, h: 11, o: 0.75, d: 90 },
    { x: 17.5, y: 9, h: 16, o: 1, d: 180 },
    { x: 22.75, y: 16, h: 9, o: 0.4, d: 270 },
  ]

  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden className="shrink-0">
      <rect width="32" height="32" rx="7" fill={bg} />
      {bars.map((b) => (
        <rect
          key={b.x}
          x={b.x}
          y={b.y}
          width="3.5"
          height={b.h}
          rx="1.25"
          fill={fg}
          opacity={b.o}
          className={animate ? 'anim-grow' : undefined}
          style={
            animate
              ? { animationDelay: `${b.d}ms`, transformOrigin: `${b.x + 1.75}px 25px` }
              : undefined
          }
        />
      ))}
    </svg>
  )
}

/* ---------------------------------------------------------------------------
   ONE CENTRED LOADER, EVER.

   The loader is fixed to the middle of the screen, so two of them mounting at
   once would stack exactly on top of each other — two logos, two labels, one
   position. Today that is nearly unreachable (the KPI strip renders nothing
   while it loads, and the permissions detail panel cannot open before its list
   has arrived), but "nearly unreachable" is a property of the CURRENT pages,
   not of this component, and the next page to be added does not know the rule.

   So the rule is enforced here: the first loader to mount owns the centre and
   the rest render nothing. If the owner unmounts while another is still open,
   ownership passes — which is why this is a subscription and not a boolean.
   --------------------------------------------------------------------------- */
let openLoaders: symbol[] = []
const subscribers = new Set<() => void>()

function publish() {
  for (const s of subscribers) s()
}

function useOwnsCentre(): boolean {
  const id = useRef<symbol>(undefined as unknown as symbol)
  if (!id.current) id.current = Symbol('loader')

  const [owner, setOwner] = useState<symbol | undefined>(() => openLoaders[0])

  useEffect(() => {
    const me = id.current
    openLoaders = [...openLoaders, me]
    const sync = () => setOwner(openLoaders[0])
    subscribers.add(sync)
    publish()
    return () => {
      openLoaders = openLoaders.filter((x) => x !== me)
      subscribers.delete(sync)
      publish()
    }
  }, [])

  return owner === id.current
}
/**
 * THE LOADER.
 *
 * The mark, alive. Four bars compressing toward their shared baseline and
 * rising again, staggered left to right — the same reading order as the chart
 * they represent.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT A SPINNER
 *
 * A rotating arc says "something is happening" and belongs to no product. This
 * mark is a bar chart, and the thing being waited for is a bar chart, so the
 * waiting state is the data arriving. It also means the one moment the platform
 * is least useful is the moment it most clearly says whose platform it is.
 *
 * ---------------------------------------------------------------------------
 * IT NEVER STOPS BEING THE LOGO
 *
 * Each bar animates around its OWN height, and all four share the y=25 baseline
 * of the mark, so the ascending-with-the-last-cut-short silhouette survives
 * every frame. Flattening them to a common height would produce an audio
 * equaliser and throw the identity away.
 *
 * ---------------------------------------------------------------------------
 * ACCESSIBILITY
 *
 * role="status" with the label as real text, so the meaning does not depend on
 * seeing motion. Under prefers-reduced-motion the global rule in styles.css
 * freezes every animation at its final keyframe — which is why that keyframe is
 * the RESTING mark: those users see a correct, still logo and read the label.
 *
 * aria-hidden on the svg: the mark is decoration once the label carries the
 * meaning, and a screen reader announcing "image" adds nothing.
 */
export function BrandLoader({
  size = 'md',
  label,
  className = '',
  inline = false,
}: {
  /**
   * Only affects the mark's size and the label's. Position no longer varies:
   * every loader is centred on the screen, which is the whole point.
   */
  size?: 'sm' | 'md' | 'lg'
  /** Say what is being waited for, not "Loading". It is read aloud. */
  label: string
  className?: string
  /**
   * Draw it IN THE FLOW instead of fixed to the centre of the screen, with no
   * veil and no single-owner rule.
   *
   * For a placeholder rather than a wait: a page that is genuinely empty and
   * says so. The fixed variant would be wrong there in two ways — it would dim
   * the whole application including the navigation for as long as the page was
   * open, and it would claim the centre against any real loader that started
   * while you sat there.
   */
  inline?: boolean
}) {
  const px = size === 'lg' ? 46 : 36
  const ownsCentre = useOwnsCentre()

  // The mark's own geometry. Kept in step with BrandMark above: same x, y,
  // height and opacity, so the loader and the logo are the same object.
  const bars = [
    { x: 7, y: 18, h: 7, o: 0.55, d: 0 },
    { x: 12.25, y: 14, h: 11, o: 0.75, d: 110 },
    { x: 17.5, y: 9, h: 16, o: 1, d: 220 },
    { x: 22.75, y: 16, h: 9, o: 0.4, d: 330 },
  ]

  // A second loader renders nothing: one thing, one place. See useOwnsCentre.
  // An inline one is exempt — it does not occupy the centre, so it cannot
  // collide with anything.
  if (!inline && !ownsCentre) return null

  return (
    /* FIXED TO THE MIDDLE OF THE SCREEN, whatever is loading and wherever on
       the page it lives.
       ------------------------------------------------------------------------
       It used to render in place — inside whichever card was waiting — so it
       appeared at a different height on every tab, and low down the page on the
       taller ones. A reader had to FIND it. Anchoring it to one position means
       the eye goes to the same spot every time and the answer to "is it doing
       something" costs no search.

       pointer-events-none: this reports, it does not block. Tabs, the currency
       control and the navigation all stay live underneath, which matters
       because a slow query is exactly when somebody wants to go elsewhere. */
    /* A UNIFORM VEIL, NOT A SHAPE BEHIND THE LOGO.
       ------------------------------------------------------------------------
       Three attempts, and the first two were the same mistake twice: a card,
       then a radial wash. Both put an OBJECT on the screen around the mark, and
       an object has an edge — the wash's soft edge read as a smudged disc, which
       is worse than the card's honest one.

       The thing actually needed is separation from whatever is underneath, and
       the way to get that without drawing anything is to quiet the entire
       viewport at once. A full-bleed veil has no edge to notice because it has
       no boundary inside the screen: it reads as the page going quiet, which is
       what is happening.

       Kept light (55%) and barely blurred, so the page stays recognisable
       behind it — this is a pause, not a modal.

       STILL pointer-events-none. The veil says "busy", it does not say
       "blocked": tabs, the currency control and the navigation all stay live,
       which matters most during exactly the slow query that put it there. */
    <div
      role="status"
      aria-live="polite"
      className={
        inline
          ? `grid place-items-center ${className}`
          : `pointer-events-none fixed inset-0 z-50 grid place-items-center bg-(--color-surface-2)/55 backdrop-blur-[1px] ${className}`
      }
    >
      {/* NOTHING BEHIND THE MARK. No card, no wash, no shape of any kind.
          --------------------------------------------------------------------
          There was a card. It was removed. It was replaced with a radial wash
          of the page colour — meant to be an edgeless fade, but a soft-edged
          light disc on a light page is still a disc, and it read as a smudge
          around the logo rather than as the page going quiet.

          Both attempts were solving the same thing: a grey label is hard to
          read over a dense grid. The right fix was not another backdrop, it was
          the LABEL — see below. So the indicator is now exactly three things
          with nothing under them: the mark, its name for what is loading, and
          the sweep. */}
      <div className="flex flex-col items-center gap-3.5">
        <svg width={px} height={px} viewBox="0 0 32 32" fill="none" aria-hidden className="shrink-0">
          <rect width="32" height="32" rx="7" fill="var(--color-navy-700)" />
          {bars.map((b) => (
            <rect
              key={b.x}
              x={b.x}
              y={b.y}
              width="3.5"
              height={b.h}
              rx="1.25"
              fill="#ffffff"
              className="anim-brand-bar"
              style={
                {
                  // The SHARED baseline. Every bar ends at y=25, so they
                  // compress toward the floor of the chart rather than toward
                  // their own middles — which would make them drift apart and
                  // stop reading as one chart.
                  transformOrigin: `${b.x + 1.75}px 25px`,
                  animationDelay: `${b.d}ms`,
                  '--bar-rest': b.o,
                  '--bar-dim': b.o * 0.4,
                } as React.CSSProperties
              }
            />
          ))}
        </svg>

        {/* THE LABEL CARRIES ITSELF NOW.
            Full-strength ink and medium weight instead of the caption grey the
            wash was there to rescue. Two shades darker and one weight heavier
            costs nothing anywhere, and it is legible over a grid without
            putting a shape on the screen to sit on. */}
        <span
          className={`font-medium text-(--color-ink) ${
            size === 'lg' ? 'text-(length:--text-base)' : 'text-(length:--text-small)'
          }`}
        >
          {label}
        </span>

        {/* A SWEEP, NOT A FILL. Nothing here knows how far along it is, and a
            bar that creeps to 90% and waits is a progress bar that lies. */}
        <span
          aria-hidden
          className="block h-[3px] w-36 overflow-hidden rounded-full bg-(--color-line)"
        >
          <span className="anim-brand-sweep block h-full w-1/3 rounded-full bg-(--color-navy-700)" />
        </span>
      </div>
    </div>
  )
}
/**
 * The wordmark.
 *
 * The second line used to read "REDACHEM ANALYTICS". Removed 4 Aug 2026: the
 * product does not belong to one customer, and a lockup that says so cannot be
 * shown to the next one. It now spells out the name — MBI stands for Mindzer BI
 * — which is true everywhere.
 */
export function Wordmark({
  tone = 'navy',
  subtitle = 'MINDZER BI',
}: {
  tone?: 'navy' | 'white'
  /** Pass an empty string to show the name alone. */
  subtitle?: string
}) {
  const primary = tone === 'navy' ? 'text-(--color-ink)' : 'text-white'
  const secondary = tone === 'navy' ? 'text-(--color-ink-3)' : 'text-white/55'

  return (
    <div className="min-w-0 leading-tight">
      <div className={`truncate text-[15px] font-semibold tracking-[-0.01em] ${primary}`}>
        MBI
      </div>
      {subtitle && (
        <div className={`truncate text-[10px] font-medium tracking-[0.11em] ${secondary}`}>
          {subtitle}
        </div>
      )}
    </div>
  )
}
